import React, { FC, useEffect, useState } from 'react'
import Image from "next/legacy/image"
import s from './HeroPDP.module.css'
import AddToCartButton from '@components/ui/AddToCartButton'
import { useTranslations } from 'next-intl'
import { useUI } from '@components/ui'
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react'
import { Keyboard, Mousewheel, Pagination, Autoplay } from 'swiper/modules'
import { components } from 'react-select'
import { Rating } from '@components/ui'
import ClickOutside from '@lib/click-outside'
import isEmpty from 'lodash.isempty'
import useAddLine from '@framework/actual-cart/use-add-line'
import Cookies from 'js-cookie'
import { useRouter } from 'next/router'
import { urlFor } from '@lib/sanity'
import { showHtml } from '@lib/utils/utils'
import LegalGuaranteeNotice from '@components/legal/LegalGuaranteeNotice'

// Get a pre-configured url-builder from your sanity client

export interface TListForm {
  amount: number
  discount: number
}

export interface CListForm {
  content: string
  highlighted?: boolean
}

export interface SellingPlan {
  sellingPlanSlug: string,
  sellingPlanId?: string,
  title: string,
  pillText?: string,
  pillColorClass?: string,
  pillTextColorClass?: string,
  default?: boolean,
  isSelected?: boolean
}

interface HeroPDPProps {
  className?: string
  headline: string
  subhead?: string
  description: string
  productImages: any
  product: any
  bestseller: boolean
  recommendation: boolean
  upsellingItems?: {
    parametersHeading?: string,
    parametersSubheading?: string,
    parameters?: {
      _id: string
      title: string
      description: string
      image: any
      productSlug?: string
      variantSlug?: string
      originalPrice: string
      discountedPrice: string
    }[],
    shippingMethodsHeading?: string
    shippingMethods?: {
      _id: string
      title: string
      type: string
      heading: string
      price: string
      description: string
      colorClass: string
      textColorClass: string
      features: {
        icon: string
        description: string
      }[]
    }[]
  }
  subscriptionConfig: {
    plans: {
      title: string
      sellingPlanSlug: string
      sellingPlanId?: string  // is later loaded from MongoDB mapping
      pillText?: string
      pillColorClass?: string
      pillTextColorClass?: string
      default?: boolean
    }[]
    benefitsTitle: string
    benefitsSubtitle: string
    benefits: {
      title: string
      description: string
      icon: string
    }[]
    faqs: {
      title: string
      description: string
    }[]
  }
  usps: any
  discounts: Array<TListForm>
  prodInfo: any
  prodInfo2: any
  howTo: any
  chips: Array<CListForm>
  rating: any
  reviewers: string
  colorDark: string
  colorLight: string
  priceBanner: string
}

const HeroPDP: FC<HeroPDPProps> = ({
                                     className,
                                     product,
                                     headline,
                                     subhead,
                                     description,
                                     productImages,
                                     bestseller,
                                     recommendation,
                                     upsellingItems,
                                     subscriptionConfig,
                                     usps,
                                     discounts,
                                     prodInfo,
                                     prodInfo2,
                                     howTo,
                                     chips,
                                     rating,
                                     reviewers,
                                     colorDark,
                                     colorLight,
                                     priceBanner
                                   }) => {

  const desktopImages = productImages?.items || []
  const mobileImages = productImages?.itemsMobile || desktopImages

  const t = useTranslations('Product')
  const { replace } = useRouter()
  const [amount, setAmount] = useState(1)
  const [discount, setDiscount] = useState(0)
  const [teaserImageMobile, setTeaserImageMobile] = useState(mobileImages.length > 0 ? urlFor(mobileImages[0]?.teaserImage).url() as any : null)
  const [teaserImageDesktop, setTeaserImageDesktop] = useState(desktopImages.length > 0 ? urlFor(desktopImages[0]?.teaserImage).url() as any : null)
  const [purchaseMode, setPurchaseMode] = useState<'one-time' | 'subscription'>(/* productHasSubscription ? 'subscription' : */ 'one-time')
  const [selectedSellingPlan, setSelectedSellingPlan] = useState<SellingPlan | undefined>(subscriptionConfig?.plans?.find(plan => plan.sellingPlanId != null && plan.default) ?? undefined)
  const [showSellingPlanDropdown, setShowSellingPlanDropdown] = useState<boolean>(false)
  const [showSellingPlanDropdownMobile, setShowSellingPlanDropdownMobile] = useState<boolean>(false)
  const [showAmountDropdown, setShowAmountDropdown] = useState<boolean>(false)
  const [showAmountDropdownMobile, setShowAmountDropdownMobile] = useState<boolean>(false)
  const addLine = useAddLine()
  const {
    openSidebar,
    setSidebarView,
    setProdInfo,
    setProdInfo2,
    setHowTo,
    setSubscriptionBenefits
  } = useUI()

  const [swiper, setSwiper] = useState(useSwiper())
  const pid = Cookies.get('pid')
  const { query } = useRouter()

  const ad = query?._sa

  // get basic (one-time purchase) variant
  let basicVariant = product?.variants?.find((variant: any) => variant?.variantSlug?.value === 'basic')
  if (basicVariant == null && product?.variants?.length > 0) {
    basicVariant = product?.variants[0]
  }

  // get subscription variant
  let subscriptionVariant = product?.variants?.find((variant: any) => variant?.variantSlug?.value === 'subscription')
  if (subscriptionVariant == null && product?.variants?.length > 0) {
    subscriptionVariant = product?.variants[0]
  }

  //Dropdown Select styling
  const DropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <span className={`ml-2 text-icon22 icon-22-CaretDown-22 `}></span>
      </components.DropdownIndicator>
    )
  }

  const productHasSubscription = subscriptionVariant != null && subscriptionConfig?.plans?.length > 0

  const changeSidebarViewAndToggle = (view = '') => {
    setProdInfo(prodInfo)
    setProdInfo2(prodInfo2)
    setHowTo(howTo)
    setSubscriptionBenefits({
      title: subscriptionConfig?.benefitsTitle,
      subtitle: subscriptionConfig?.benefitsSubtitle,
      benefits: subscriptionConfig?.benefits,
      faqs: subscriptionConfig?.faqs
    })
    setSidebarView(view)
    openSidebar()
  }

  const amountSelectCustomStyles: any = {
    control: (base: any, state: any) => ({
      ...base,
      background: 'transparent',
      borderRadius: 12,
      ['@media (min-width: 640px)']: {
        height: '70.2px'
      },
      height: '59px',
      paddingLeft: '5px',
      paddingRight: '10px',
      paddingTop: '0px',
      color: 'black',
      fontSize: '17px',
      borderColor: 'transparent',
      outlineColor: 'transparent',
      fontWeight: 300,
      // Removes weird border around container
      boxShadow: state.isFocused ? null : null,
      '&:hover': {
        // Overwrittes the different states of border
        borderColor: state.isFocused ? 'black' : 'transparent'
      }
    }),
    dropdownIndicator: (base: any) => ({
      ...base,
      color: '#1c1c1c' // Custom colour

    }),
    singleValue: (provided: any, state: any) => ({
      ...provided,
      color: 'inherit',
      left: '10px',
      right: '0',
      width: '100%'
    }),
    menu: (base: object) => ({
      ...base,
      // override border radius to match the box
      height: '100%',
      width: '100%',
      borderRadius: 12,
      position: 'absolute',
      fontWeight: 300,
      top: -12,
      left: 0,
      right: 0,
      // kill the gap
      marginTop: 12,
      zIndex: '100'
    }),

    menuList: (base: object) => ({
      ...base,
      // kill the white space on first and last option
      borderRadius: 12,
      padding: 0,
      boxShadow: 'var(--shadowS)',
      border: '1px solid #111'
    }),


    indicatorSeparator: (base: object) => ({
      ...base,
      display: 'none'
    }),


    option: (styles: any, { data, isDisabled, isFocused, isSelected }: any) => {
      // const color = chroma(data.color);

      return {
        ...styles,
        backgroundColor: 'white',
        color: '#1c1c1c',
        padding: '20px 10px',
        borderBottom: 'rgba(28,28,28,0.2)'
      }
    }
  }

  const updateAmount = (amount: number, discount: number) => {
    setAmount(amount)
    setDiscount(discount)
  }

  let amountOptions: any[] = [{
    value: 1, label: <div className={s.option}>
      <span className={s.amountInfo + ' ' + s.amountEl}>{t('oneTimePurchase')}</span>
      <span
        className={s.newPriceInfo + ' ' + s.amountEl}>{parseFloat(basicVariant?.price ?? product.price.value).toFixed(2)}€</span>
    </div>
  }]

  let amountOptionsDesktop: any[] = [{
    value: 1, label: <div className={s.option}>
      <span className={s.amountInfo + ' ' + s.amountEl}>1 Kit</span>
      <span
        className={s.newPriceInfo + ' ' + s.amountEl}>{parseFloat(basicVariant?.price ?? product.price.value).toFixed(2)}€</span>
    </div>
  }]

  discounts?.forEach(item => {
    amountOptions.push({
      value: item.amount, label: <div className={s.option}>
        <span className={s.amountInfo + ' ' + s.amountEl}>{item.amount} {t('units')}</span>
        <span className={s.discountInfo + ' ' + s.amountEl + ' bg-primary500'}>- {item.discount}%</span>
        <span
          className={s.newPriceInfo + ' ' + s.amountEl}>{(parseFloat(basicVariant?.price ?? product.price.value) * item.amount * ((100 - item.discount) / 100)).toFixed(2)}€</span>
        <span
          className={s.oldPriceInfo + ' ' + s.amountEl}>{(parseFloat(basicVariant?.price ?? product.price.value) * item.amount).toFixed(2)}€</span>
      </div>
    })
  })

  // if(discounts && discounts.length > 0) {
  //   for (let i = 0; i < 3; i++) {
  //     const item = discounts[0]
  //     amountOptions.push({
  //       value: (i + 2), label: <div className={s.option}>
  //         <span className={s.amountInfo + ' ' + s.amountEl}>{(i + 2)} {t('units')}</span>
  //         <span className={s.discountInfo + ' ' + s.amountEl + ' bg-primary500'}>{item?.discount}% off</span>
  //         <span
  //           className={s.newPriceInfo + ' ' + s.amountEl}>{(parseFloat(basicVariant?.price ?? product.price.value) * (i + 2) * ((100 - item?.discount) / 100)).toFixed(2)}€</span>
  //         <span
  //           className={s.oldPriceInfo + ' ' + s.amountEl}>{(parseFloat(basicVariant?.price ?? product.price.value) * (i + 2)).toFixed(2)}€</span>
  //       </div>
  //     })
  //     amountOptionsDesktop.push({
  //       value: (i + 2), label: <div className={s.option}>
  //         <span className={s.amountInfo + ' ' + s.amountEl}>{(i + 2)} {t('units')}</span>
  //         <span className={s.discountInfo + ' ' + s.amountEl + ' bg-primary500'}>{item?.discount}% off</span>
  //         <span
  //           className={s.newPriceInfo + ' ' + s.amountEl}>{(parseFloat(basicVariant?.price ?? product.price.value) * (i + 2) * ((100 - item?.discount) / 100)).toFixed(2)}€</span>
  //         <span
  //           className={s.oldPriceInfo + ' ' + s.amountEl}>{(parseFloat(basicVariant?.price ?? product.price.value) * (i + 2)).toFixed(2)}€</span>
  //       </div>
  //     })
  //   }
  // }

  const toggleShowSellingPlanDropdown = () => {
    setShowSellingPlanDropdown(currentValue => !currentValue)
  }

  const toggleShowSellingPlanDropdownMobile = () => {
    setShowSellingPlanDropdownMobile(currentValue => !currentValue)
  }

  const toggleShowAmountDropdownMobile = () => {
    setShowAmountDropdownMobile(currentValue => !currentValue)
  }

  const toggleShowAmountDropdown = () => {
    setShowAmountDropdown(currentValue => !currentValue)
  }

  const addSubscriptionToCart = (skipCheckoutConflictCheck?: boolean) => {
    if (selectedSellingPlan == null || selectedSellingPlan?.sellingPlanId == null) return

    addLine({
      merchandiseId: subscriptionVariant?.id,
      quantity: 1,
      sellingPlanId: selectedSellingPlan.sellingPlanId
    })
      .then(async (result: any) => {
        // added successfully
        await replace('/cart')
      })
      .catch((error: any) => {
        console.error(error)
      })
  }


  useEffect(() => {
    if (discounts && discounts[0].amount === 1) {
      updateAmount(discounts[0].amount, discounts[0].discount)
    }
  }, [discounts])

  return (
    <div className={s.root}>
      <div className={s.grid + ' grid'}>
        <div className={'lg:pr-10 relative'}>
          <div
            className='block aspect-w-14 lg:aspect-w-16 mt-space10 lg:mt-space20 mb-10 lg:rounded-cornerS overflow-hidden aspect-h-16 lg:aspect-h-14 relative'>
            {mobileImages && mobileImages.length > 0 && teaserImageMobile && (
              <Image
                alt="Product hero image"
                src={teaserImageMobile}
                layout="fill"
                objectFit="cover"
                objectPosition="center center"
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="block md:hidden"
                priority
              />
            )}
            {desktopImages && desktopImages.length > 0 && teaserImageDesktop && (
              <Image
                alt="Product hero image"
                src={teaserImageDesktop}
                layout="fill"
                objectFit="cover"
                objectPosition="center center"
                sizes="(max-width: 1024px) 100vw, 50vw"
                className="hidden md:block"
                priority
              />
            )}
            {bestseller && (<div className={'inline-block mr-4'}>
              <div
                className={s.bestseller + s.teaserInfo + ' absolute top-space20 md:top-space7 right-space6 md:right-space7 text-center bg-primary400 inline-block rounded-cornerS px-space3 md:px-space6 text-neutral900 text-paragraph03 mb-4 font-medium py-space2 md:py-space3'}>{t('bestseller')}</div>
            </div>)}
            {recommendation && (<div className={'inline-block mr-4'}>
              <div
                className={s.recommended + s.teaserInfo + ' absolute top-space20 md:top-space7 right-space6 md:right-space7 text-center bg-primary100 inline-block rounded-cornerS px-space3 md:px-space6 text-neutral900 text-paragraph03 font-medium py-space2 md:py-space3'}>{t('recommended')}</div>
            </div>)}
            <div className={'inline-block mr-4 block md:hidden'}>
              <div className={s.teaserImages}>
                {mobileImages && mobileImages.length > 0 && mobileImages.map((image: any, index: string) => {
                  return <span key={'productImageMobile' + index} onClick={() => {
                    setTeaserImageMobile(urlFor(image?.teaserImage).url() as any)
                  }}
                               className={'cursor-pointer w-e50 h-e50 m-space1 rounded-cornerXS border border-solid border-neutral700 inline-block overflow-hidden'}><Image
                    alt={'Product hero image preview' + index} priority={true} loading='eager'
                    src={urlFor(image?.teaserImage).url() as any} width={50} height={50}
                    objectPosition={'center center'} /></span>
                })}
              </div>
            </div>
            <div className={'inline-block mr-4 hidden md:block'}>
              <div className={s.teaserImages}>
                {desktopImages && desktopImages.length > 0 && desktopImages.map((image: any, index: string) => {
                  return <span key={'productImageDesktop' + index} onClick={() => {
                    setTeaserImageDesktop(urlFor(image?.teaserImage).url() as any)
                  }}
                               className={'cursor-pointer w-e50 h-e50 m-space1 rounded-cornerXS border border-solid border-neutral700 inline-block overflow-hidden'}><Image
                    alt={'Product hero image preview' + index} priority={true} loading='eager'
                    src={urlFor(image?.teaserImage).url() as any} width={50} height={50}
                    objectPosition={'center center'} /></span>
                })}
              </div>
            </div>
          </div>
        </div>
        <div className='min-w-0'>
          <div className={'block mt-minus-24 md:mt-space24 mb-space4 md:mb-10 w-full xl:max-w-e500 px-space6 md:px-0'}>
            <h1
              className={'text-heading03Mobile text-neutral900 font-medium mt-space4'}>{headline + (ad ? ' ' + t('inclusive') + ' ' + t(ad) : '')}</h1>
            {reviewers && <div><Rating value={rating} light={true} /><span
              className={'text-paragraph02 font-medium text-neutral900'}> {rating}</span><span
              className={'font-mono text-button text-neutral600'}> | {reviewers} {t('reviewers')}</span></div>
            }
            <p className={'text-neutral700 text-paragraph02 ' + (reviewers ? '' : 'mt-space6')} dangerouslySetInnerHTML={showHtml(description)}>
            </p>
            <div className={(discounts && discounts.length > 0 ? '' : 'mb-space4')}>
              <ul className={s.chipsWrapper}>
                {chips && chips.map((item, index) => {
                  return <li
                    className={s.chip + ' ' + (item?.highlighted ? 'bg-tertiary500 text-neutral50' : 'bg-neutral400')}
                    style={colorDark ? { backgroundColor: colorDark + '!important' } : undefined}
                    key={index}>
                    <p className={`${s.chipText}`}>{item.content}</p>
                  </li>
                })}
              </ul>
            </div>

            <div className={'block'}>
              {productHasSubscription &&
                <div className='grid grid-cols-2 pt-space2 gap-space2'>
                  <button
                    className={`w-full text-neutral900 border border-neutral500 hover:bg-neutral50 ${purchaseMode === 'subscription' ? 'border-neutral900 bg-neutral50' : ''} text-button p-space5 rounded-cornerXS cursor-pointer flex justify-center items-center flex-wrap`}
                    onClick={() => setPurchaseMode('subscription')}>
                    <span className='text-paragraph02 font-medium'>{t('subscription')}<span
                      style={colorLight ? { backgroundColor: colorLight + '!important' } : undefined}
                      className={s.discountInfo + ' bg-primary500 ml-space4 font-normal ' + s.amountEl}>- 20%</span></span>
                  </button>

                  <button
                    className={`w-full text-neutral900 border border-neutral500 hover:bg-neutral50 ${purchaseMode === 'one-time' ? 'border-neutral900 bg-neutral50' : ''} text-button p-space5 rounded-cornerXS cursor-pointer`}
                    onClick={() => setPurchaseMode('one-time')}>
                    <span className='text-paragraph02 font-medium'>{t('oneTimePurchase')}</span>
                  </button>
                </div>
              }

              {/* <!-- One Time Purchase --> */}
              {((purchaseMode === 'one-time' || !productHasSubscription) && discounts && discounts.length > 0) && (
                <>
                  <p className={'pt-space8 pb-2 text-paragraph02 font-medium'}>{t('selectAmount')}</p>
                  <div className={s.amountWrapper + ' mb-space6'}>
                    {discounts && discounts[0].amount !== 1 &&
                      <div className={amount === 1 ? s.active : ''} onClick={() => updateAmount(1, 0)}>
                      <span className={s.radioCheck + ' ' + s.amountEl}><span
                        className={s.radioCheckHelper}></span></span>
                        <span className={s.amountInfo + ' ' + s.amountEl}>1 {t('unit')}</span>
                        <span
                          className={s.newPriceInfo + ' ' + s.amountEl}>{(parseFloat(basicVariant?.price ?? product.price.value)).toFixed(2)}€</span>
                      </div>
                    }
                    {discounts && discounts.map((item, index) => {
                      return <div key={'discount' + index} className={amount == item.amount ? s.active : ''}
                                  onClick={() => updateAmount(item.amount, item.discount)}>
                        <span className={s.radioCheck + ' ' + s.amountEl}><span
                          className={s.radioCheckHelper}></span></span>
                        <span className={s.amountInfo + ' ' + s.amountEl}>{item.amount} {t('units')}</span>
                        <span style={colorLight ? { backgroundColor: colorLight + '!important' } : undefined} className={s.discountInfo + ' bg-primary500 ' + s.amountEl}>- {item.discount}%</span>
                        <span
                          className={s.newPriceInfo + ' ' + s.amountEl}>{(parseFloat(basicVariant?.price ?? product.price.value) * ((100 - item.discount) / 100)).toFixed(2)}€</span>
                        <span
                          className={s.oldPriceInfo + ' ' + s.amountEl}>{(parseFloat(basicVariant?.price ?? product.price.value)).toFixed(2)}€</span>
                      </div>
                    })
                    }
                  </div>
                </>
              )}

              {/*One Time Purchase */}
              {/* { (purchaseMode === 'one-time' || !productHasSubscription) && (*/}
              {/*  <>*/}
              {/*      <ClickOutside active={showAmountDropdown} onClick={() => setShowAmountDropdown(false)}>*/}
              {/*        <div className={'relative ' + (!productHasSubscription ? 'pt-space8 ' : '')} >*/}
              {/*          <div*/}
              {/*            className={`mt-space2 px-space4 py-space5 border border-neutral500 font-medium rounded-cornerXS bg-neutral50 cursor-pointer text-neutral900 flex justify-between items-center`}*/}
              {/*            onClick={() => {*/}
              {/*              setPurchaseMode('one-time');*/}
              {/*              toggleShowAmountDropdown();*/}
              {/*            }}>*/}
              {/*            <div className={'inline_block ' + s.amountLabel}>*/}
              {/*              { amount != null && (*/}
              {/*                <div className='items-center font-medium'>*/}
              {/*                  <span>{amountOptionsDesktop[(amount-1)]?.label}</span>*/}
              {/*                </div>*/}
              {/*              )}*/}
              {/*            </div>*/}
              {/*            <div className='items-center float-right inline_block'>*/}
              {/*              <span className={`ml-2 text-icon22 icon-22-Caret${ showAmountDropdown ? 'Up' : 'Down' }-22 `}></span>*/}
              {/*            </div>*/}
              {/*          </div>*/}

              {/*          { showAmountDropdown && (*/}
              {/*            <div className='pt-space4 pb-space2 border border-t-0 border-neutral500 rounded-cornerXS rounded-t-none bg-neutral50 absolute left-0 right-0 top-full transform -translate-y-space4 z-10 pointer-events-none'>*/}
              {/*              <div className='px-space4 pb-space2'>*/}
              {/*                <hr />*/}
              {/*              </div>*/}


              {/*              { amountOptionsDesktop.map(*/}
              {/*                (option, index) => (*/}
              {/*                  <div*/}
              {/*                    key={`amount-option-${index}`}*/}
              {/*                    className='px-space4 py-space3 cursor-pointer pointer-events-auto items-center'*/}
              {/*                    onClick={() => {*/}
              {/*                      setAmount(option.value);*/}
              {/*                      setShowAmountDropdown(false);*/}
              {/*                    }}>*/}
              {/*                    <div className='items-center'>*/}
              {/*                      <span>{option.label}</span>*/}
              {/*                    </div>*/}
              {/*                  </div>*/}
              {/*                )*/}
              {/*              )}*/}
              {/*            </div>*/}
              {/*          )}*/}
              {/*        </div>*/}
              {/*      </ClickOutside>*/}
              {/*  </>*/}
              {/*)}*/}

              {/* <!-- Subscription --> */}
              {purchaseMode === 'subscription' && productHasSubscription && (
                <ClickOutside active={showSellingPlanDropdown} onClick={() => setShowSellingPlanDropdown(false)}>
                  <div className='relative'>
                    <div
                      className='mb-space2 md:mb-0 mt-space2 p-space4 border border-neutral500 rounded-cornerXS bg-neutral50 cursor-pointer text-neutral900 flex justify-between items-center'
                      onClick={toggleShowSellingPlanDropdown}>
                      <div>
                        {selectedSellingPlan == null &&
                          <span className='text-neutral600'>{t('selectDeliveryFrequency')}</span>}
                        {selectedSellingPlan != null && (
                          <div className='flex justify-start items-center'>
                            <span>{selectedSellingPlan.title}</span>
                            {!isEmpty(selectedSellingPlan.pillText) && (
                              <span
                                className={`ml-space2 ${s.discountInfo} ${s.amountEl} bg-${selectedSellingPlan.pillColorClass ?? 'primary100'} text-${selectedSellingPlan.pillTextColorClass ?? 'primary100'}`}>{selectedSellingPlan.pillText}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className='flex justify-end items-center'>
                        {selectedSellingPlan != null && subscriptionVariant != null &&
                          <span className='ml-space2'>{subscriptionVariant.price?.toFixed(2)}€</span>}
                        <span
                          className={`ml-2 text-icon22 icon-22-Caret${showSellingPlanDropdown ? 'Up' : 'Down'}-22 `}></span>
                      </div>
                    </div>

                    {showSellingPlanDropdown && (
                      <div
                        className='pt-space4 pb-space2 border border-t-0 border-neutral500 rounded-cornerXS rounded-t-none bg-neutral50 absolute left-0 right-0 top-full transform -translate-y-space4 z-10 pointer-events-none'>
                        <div className='px-space4 pb-space2'>
                          <hr />
                        </div>


                        {subscriptionConfig?.plans != null && subscriptionConfig.plans.filter(plan => plan?.sellingPlanId != null).map(
                          (plan, index) => (
                            <div
                              key={`selling-plan-${index}`}
                              className='px-space4 py-space3 cursor-pointer pointer-events-auto flex justify-between items-center'
                              onClick={() => {
                                setSelectedSellingPlan(plan)
                                setShowSellingPlanDropdown(false)
                              }}>
                              <div className='flex justify-start items-center'>
                                <span>{plan.title}</span>
                                {!isEmpty(plan.pillText) && (
                                  <span
                                    className={`ml-space2 ${s.discountInfo} ${s.amountEl} bg-${plan.pillColorClass ?? 'primary100'} text-${plan.pillTextColorClass ?? 'primary100'}`}>{plan.pillText}</span>
                                )}
                              </div>
                              {subscriptionVariant != null &&
                                <span className='ml-space2'>{subscriptionVariant.price?.toFixed(2)}€</span>}
                            </div>
                          )
                        )}

                        <div className='px-space4 pt-space3 pb-space4 pointer-events-auto'>
                          <hr />
                        </div>

                        <div className='px-space4 pb-space3 pointer-events-auto text-paragraph03 text-neutral600'>
                          {t('hassleFreeCancellation')}
                        </div>
                      </div>
                    )}
                  </div>
                </ClickOutside>
              )}
            </div>

            {productHasSubscription && purchaseMode === 'subscription' &&
              <div className='flex justify-center items-center text-neutral600 md:pt-space6 pb-space4 md:pb-0'>
                <span className='icon-32-Smiley-32 text-icon32 mr-space1'></span>
                <span>{t('saveBySubscribing')}</span>
              </div>}

            {purchaseMode === 'one-time' && discounts && discounts.length > 0 &&
              <div className='flex justify-center items-center text-neutral600 md:pt-space2 pb-space4 md:pb-0'>
                <span className='icon-32-Smiley-32 text-icon32 mr-space1'></span>
                <span>{t('oneTimeInfo')}</span>
              </div>}

            {priceBanner && <div className={s.priceBanner} style={colorLight ? { backgroundColor: colorLight } : undefined}>
              <div dangerouslySetInnerHTML={showHtml(priceBanner)}></div>
            </div>}

            <div className={'block md:pb-space6 md:pt-space4'}>
              {purchaseMode === 'one-time' && (
                <AddToCartButton active={basicVariant.availableForSale} amount={amount} product={product}
                                 key={product.id}
                                 labelAtc={`${t('atc')}${basicVariant != null ? ` - ${(basicVariant.price * amount * (discount > 0 ? ((100 - discount) / 100) : 1)).toFixed(2)}€` : ''}`}
                                 upsell={{
                                   parametersStep: true,
                                   shippingStep: false,
                                   subscriptionStep: true,
                                   upsellingItems,
                                   subscriptionConfig: subscriptionConfig,
                                   product: {
                                     slug: product?.slug,
                                     title: headline,
                                     price: basicVariant?.price ?? product?.price?.value,
                                     image: teaserImageDesktop
                                   }
                                 }} />
              )}

              {purchaseMode === 'subscription' && (
                <button
                  className={`${s.subscriptionToCartButton} w-full text-neutral50 bg-tertiary800 hover:bg-tertiary500 font-medium font-mono text-button py-space6 px-space10 rounded-cornerXS cursor-pointer uppercase`}
                  disabled={!subscriptionVariant.availableForSale}
                  onClick={() => addSubscriptionToCart()}>
                  {subscriptionVariant.availableForSale && (
                    <>
                      {t('atc')}{subscriptionVariant != null &&
                      <span> - {subscriptionVariant.price?.toFixed(2)}€</span>}
                    </>
                  )}
                  {!subscriptionVariant.availableForSale && (
                    <>{t('outOfStock')}</>
                  )}
                </button>
              )}

              {/* EU legal-guarantee notice — pre-contractual, directly below the
                  add-to-cart button in every purchase mode, centred on the
                  button's width (same container, the button is w-full). */}
              <div className='flex justify-center text-neutral600 pt-space4'>
                <LegalGuaranteeNotice variant="trigger" />
              </div>
            </div>


            <div className={'hidden my-space5'}>
              <div className={'grid grid-cols-1 relative'}>

                {/* <!-- One Time Purchase --> */}
                <div className={'pt-2 pb-space2 ' + (purchaseMode === 'one-time' ? 'bg-neutral50' : '')}>

                  <ClickOutside active={showAmountDropdownMobile} onClick={() => setShowAmountDropdownMobile(false)}>
                    <div className='relative pb-space2'>
                      <div
                        className={`p-space4 rounded-cornerXS border border-neutral500 hover:bg-neutral50 ${purchaseMode === 'one-time' ? 'border-neutral900 bg-neutral50' : ''} cursor-pointer text-neutral900 items-center`}
                        onClick={() => {
                          setPurchaseMode('one-time')
                          toggleShowAmountDropdownMobile()
                        }}>
                        <div className={'inline_block ' + s.amountLabel}>
                          {amount != null && (
                            <div className='items-center'>
                              <span>{amountOptions[(amount - 1)]?.label}</span>
                            </div>
                          )}
                        </div>
                        <div className='items-center float-right inline_block'>
                          <span
                            className={`ml-2 text-icon22 icon-22-Caret${showAmountDropdownMobile ? 'Up' : 'Down'}-22 `}></span>
                        </div>
                      </div>

                      {showAmountDropdownMobile && (
                        <div
                          className='pt-space4 pb-space2 border border-t-0 border-neutral900 rounded-cornerXS rounded-t-none bg-neutral50 absolute left-0 right-0 top-full transform -translate-y-space4 z-10 pointer-events-none'>
                          <div className='px-space4 pb-space2'>
                            <hr />
                          </div>


                          {amountOptions.map(
                            (option, index) => (
                              <div
                                key={`amount-option-${index}`}
                                className='px-space4 py-space3 cursor-pointer pointer-events-auto items-center'
                                onClick={() => {
                                  setAmount(option.value)
                                  setShowAmountDropdownMobile(false)
                                }}>
                                <div className='items-center'>
                                  <span>{option.label}</span>
                                </div>
                              </div>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  </ClickOutside>

                  {/*<button*/}
                  {/*  className={`w-full text-neutral900 border border-neutral500 hover:bg-neutral50 ${ purchaseMode === 'one-time' ? 'border-neutral900 bg-neutral50' : '' } text-button px-space4 py-space5 rounded-cornerXS cursor-pointer flex justify-between`}*/}
                  {/*  onClick={() => setPurchaseMode('one-time')}>*/}
                  {/*  <span className='text-paragraph02'>{t('oneTimePurchase')}</span>*/}
                  {/*  <span className='text-paragraph02'>{parseFloat(basicVariant?.price ?? product.price.value).toFixed(2)}€</span>*/}
                  {/*</button>*/}
                </div>
                {/* <!-- End One Time Purchase --> */}

                {/* <!-- Subscription --> */}
                {productHasSubscription && <ClickOutside active={showSellingPlanDropdownMobile}
                                                         onClick={() => setShowSellingPlanDropdownMobile(false)}>
                  <div className='relative pb-space2'>
                    <div
                      className={`p-space4 rounded-cornerXS border border-neutral500 hover:bg-neutral50 ${purchaseMode === 'subscription' ? 'border-neutral900 bg-neutral50' : ''} cursor-pointer text-neutral900 flex justify-between items-center`}
                      onClick={() => {
                        setPurchaseMode('subscription')
                        toggleShowSellingPlanDropdownMobile()
                      }}>
                      <div>
                        {selectedSellingPlan == null &&
                          <span className='text-neutral600'>{t('selectDeliveryFrequency')}</span>}
                        {selectedSellingPlan != null && (
                          <div className='flex justify-start items-center'>
                            <span>{selectedSellingPlan.title}</span>
                            {!isEmpty(selectedSellingPlan.pillText) && (
                              <span
                                className={`ml-space2 ${s.discountInfo} ${s.amountEl} bg-${selectedSellingPlan.pillColorClass ?? 'primary100'} text-${selectedSellingPlan.pillTextColorClass ?? 'primary100'}`}>{selectedSellingPlan.pillText}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className='flex justify-end items-center'>
                        {selectedSellingPlan != null && subscriptionVariant != null &&
                          <span className='ml-space2'>{subscriptionVariant.price?.toFixed(2)}€</span>}
                        <span
                          className={`ml-2 text-icon22 icon-22-Caret${showSellingPlanDropdownMobile ? 'Up' : 'Down'}-22 `}></span>
                      </div>
                    </div>

                    {showSellingPlanDropdownMobile && (
                      <div
                        className='pt-space4 pb-space2 border border-t-0 border-neutral900 rounded-cornerXS rounded-t-none bg-neutral50 absolute left-0 right-0 top-full transform -translate-y-space4 z-10 pointer-events-none'>
                        <div className='px-space4 pb-space2'>
                          <hr />
                        </div>


                        {subscriptionConfig?.plans != null && subscriptionConfig.plans.filter(plan => plan?.sellingPlanId != null).map(
                          (plan, index) => (
                            <div
                              key={`selling-plan-${index}`}
                              className='px-space4 py-space3 cursor-pointer pointer-events-auto flex justify-between items-center'
                              onClick={() => {
                                setSelectedSellingPlan(plan)
                                setShowSellingPlanDropdownMobile(false)
                              }}>
                              <div className='flex justify-start items-center'>
                                <span>{plan.title}</span>
                                {!isEmpty(plan.pillText) && (
                                  <span
                                    className={`ml-space2 ${s.discountInfo} ${s.amountEl} bg-${plan.pillColorClass ?? 'primary100'} text-${plan.pillTextColorClass ?? 'primary100'}`}>{plan.pillText}</span>
                                )}
                              </div>
                              {subscriptionVariant != null &&
                                <span className='ml-space2'>{subscriptionVariant.price?.toFixed(2)}€</span>}
                            </div>
                          )
                        )}

                        <div className='px-space4 pt-space3 pb-space4 pointer-events-auto'>
                          <hr />
                        </div>

                        <div className='px-space4 pb-space3 pointer-events-auto text-paragraph03 text-neutral600'>
                          {t('hassleFreeCancellation')}
                        </div>
                      </div>
                    )}
                  </div>
                </ClickOutside>}
                {/* <!-- End Subscription --> */}

                {productHasSubscription && purchaseMode === 'subscription' &&
                  <div className='flex justify-center items-center text-neutral600 pb-space4'>
                    <span className='icon-32-Smiley-32 text-icon32 mr-space1'></span>
                    <span>{t('saveBySubscribing')}</span>
                  </div>}

                <div className={'pb-space10 md:pb-space4'}>
                  {purchaseMode === 'one-time' && (
                    <AddToCartButton active={basicVariant.availableForSale} amount={amount} product={product}
                                     key={product.id}
                                     labelAtc={`${t('atc')}${basicVariant != null ? ` - ${(basicVariant.price * amount * (discount > 0 ? ((100 - discount) / 100) : 1)).toFixed(2)}€` : ''}`}
                                     discounts={discounts}
                                     upsell={{
                                       parametersStep: true,
                                       shippingStep: false,
                                       subscriptionStep: true,
                                       subscriptionConfig: subscriptionConfig,
                                       upsellingItems,
                                       product: {
                                         slug: product?.slug,
                                         title: headline,
                                         price: basicVariant?.price ?? product?.price?.value,
                                         image: teaserImageDesktop
                                       }
                                     }} />
                  )}

                  {purchaseMode === 'subscription' && (
                    <button
                      className={`${s.subscriptionToCartButton} w-full text-neutral50 bg-tertiary800 hover:bg-tertiary500 font-medium font-mono text-button p-space6 rounded-cornerXS cursor-pointer uppercase`}
                      disabled={!subscriptionVariant.availableForSale}
                      onClick={() => addSubscriptionToCart()}>
                      {subscriptionVariant.availableForSale && (
                        <>
                          {t('atc')}{subscriptionVariant != null &&
                          <span> - {subscriptionVariant.price?.toFixed(2)}€</span>}
                        </>
                      )}
                      {!subscriptionVariant.availableForSale && (
                        <>{t('outOfStock')}</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className='mt-space6 border-neutral400 border-solid border-t'>
              {subscriptionConfig?.benefits?.length > 0 && <div>
                <p
                  className={'text-neutral900 text-paragraph02 font-medium cursor-pointer pb-6 pt-space4 border-neutral400 border-solid border-b'}
                  onClick={() => changeSidebarViewAndToggle('SUBSCRIPTION_BENEFITS')}>{t('subscriptionBenefits')}<span
                  className={'float-right text-icon22 icon-22-CaretRight-22'}></span></p>
              </div>}
              {prodInfo && <div>
                <p
                  className={'text-neutral900 text-paragraph02 font-medium cursor-pointer py-6 border-neutral400 border-solid border-b'}
                  onClick={() => changeSidebarViewAndToggle('PRODUCT_INFO')}>{prodInfo.cta ? prodInfo.cta : t('prodInfo')}<span
                  className={'float-right text-icon22 icon-22-CaretRight-22'}></span></p>
              </div>}
              {prodInfo2 && <div>
                <p
                  className={'text-neutral900 text-paragraph02 font-medium cursor-pointer py-6 border-neutral400 border-solid border-b'}
                  onClick={() => changeSidebarViewAndToggle('PRODUCT_INFO2')}>{prodInfo2.cta ? prodInfo2.cta : t('prodInfo')}<span
                  className={'float-right text-icon22 icon-22-CaretRight-22'}></span></p>
              </div>}
              {howTo && <div>
                <p
                  className={'text-neutral900 text-paragraph02 font-medium cursor-pointer py-6 border-neutral400 border-solid border-b'}
                  onClick={() => changeSidebarViewAndToggle('HOW_TO')}>{howTo.cta ? howTo.cta : t('howTo')}<span
                  className={'float-right text-icon22 icon-22-CaretRight-22'}></span></p>
              </div>}
              <div className={'border-neutral400 border-b border-solid'}>
                {usps && usps?.length > 1 &&
                  <Swiper
                    onSlideChange={(_s: unknown) => {
                    }}
                    cssMode={true}
                    slidesPerView={1}
                    navigation={false}
                    pagination={{
                      clickable: true
                    }}
                    mousewheel={true}
                    keyboard={true}
                    loop={true}
                    scrollbar={{
                      draggable: false
                    }}
                    onSwiper={(s: any) => {
                      setSwiper(s)
                    }}
                    modules={[Mousewheel, Keyboard, Pagination, Autoplay]}
                    className={s.mySwiper + ' pagination'}

                  >
                    {usps && usps.map((item: any, index: number) => {
                        return <SwiperSlide key={'usp' + index} className={s.swiperSlide}>
                          <div className={'text-center mt-space3 mb-1'}>
                            <span className={item.icon + ' mr-2 text-icon22'}></span>
                            <span className={'align-top text-paragraph03'}>{item.content}</span>
                          </div>
                        </SwiperSlide>
                      }
                    )}
                  </Swiper>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeroPDP
